import { CampaignStatDaily, CampaignStatOutput } from '@/api/gql/graphql';
import { SelectDropDown } from '@/components/Common';
import Chart from '@/components/Ui/Chart/Chart';
import { ChartType } from '@/components/Ui/Chart/Chart.types';
import { memo, useMemo, useState } from 'react';
import { useFormatDate } from '@/hooks/useFormatDate';

type ChartSectionProps = {
  campaignDailyStats: CampaignStatDaily[];
  campaignStats: CampaignStatOutput;
};

const CampaignVisibility = memo(
  ({ campaignDailyStats }: { campaignDailyStats: CampaignStatDaily[] }) => {
    const [filterChart, setFilterChart] = useState<'clicks' | 'views'>('views');
    const { formatShortDate } = useFormatDate();

    const dayLabels = useMemo(() => {
      return (
        campaignDailyStats?.map((stat) => formatShortDate(stat.date)) || []
      );
    }, [campaignDailyStats, formatShortDate]);

    return (
      <div className="mb-3">
        <div className="flex justify-between items-center bg-black px-6 pt-4">
          <div>
            <p className="text-white text-[15.102px] font-normal tracking-[-0.519px]">
              Visibilité de la campagne
            </p>
            <p className="text-textColor text-xs font-normal tracking-[-0.519px]">
              Nombre d'utilisateur ayant vu l'annonce
            </p>
          </div>
          <div>
            <SelectDropDown
              options={[
                { name: 'Vues', _id: 'views' },
                { name: 'Clicks', _id: 'clicks' },
              ]}
              placeholder="Vues"
              name="filterType"
              handleChange={(option) =>
                setFilterChart(option._id as 'clicks' | 'views')
              }
              className="!text-white !text-xs"
              initialSelectedOptions={
                filterChart === 'views'
                  ? { name: 'Vues', _id: 'views' }
                  : { name: 'Clics', _id: 'clicks' }
              }
            />
          </div>
        </div>
        <Chart
          stepSize={1}
          type={ChartType.BAR}
          data={campaignDailyStats?.map((item) => item[filterChart]) || []}
          labels={dayLabels}
        />
      </div>
    );
  },
);
CampaignVisibility.displayName = 'CampaignVisibility';

const RegionView = memo(
  ({ campaignStats }: { campaignStats: CampaignStatOutput }) => {
    const [filterRegion, setFilterRegion] = useState<'clicks' | 'views'>(
      'views',
    );

    return (
      <div className="mb-3">
        <div className="flex justify-between items-center bg-black px-6 pt-4">
          <div>
            <p className="text-white text-[15.102px] font-normal tracking-[-0.519px]">
              Vue par région
            </p>
            <p className="text-textColor text-xs font-normal tracking-[-0.519px]">
              Nombre de vue total par région
            </p>
          </div>
          <div>
            <SelectDropDown
              options={[
                { name: 'Vues', _id: 'views' },
                { name: 'Clicks', _id: 'clicks' },
              ]}
              placeholder="Vues"
              name="filterType"
              handleChange={(option) =>
                setFilterRegion(option._id as 'clicks' | 'views')
              }
              className="!text-white !text-xs"
              initialSelectedOptions={
                filterRegion === 'views'
                  ? { name: 'Vues', _id: 'views' }
                  : { name: 'Clics', _id: 'clicks' }
              }
            />
          </div>
        </div>
        <Chart
          stepSize={1}
          type={ChartType.BAR}
          data={
            campaignStats?.byRegion?.map((item) => item[filterRegion]) || []
          }
          labels={campaignStats?.byRegion?.map((item) => item.region) || []}
        />
      </div>
    );
  },
);
RegionView.displayName = 'RegionView';

const ChartSection: React.FC<ChartSectionProps> = ({
  campaignDailyStats,
  campaignStats,
}) => {
  return (
    <>
      <CampaignVisibility campaignDailyStats={campaignDailyStats} />
      <RegionView campaignStats={campaignStats} />
    </>
  );
};
ChartSection.displayName = 'ChartSection';

export default ChartSection;
